<!-- Responsibility: Render the reusable info/entity dialog content surface.
Scope: Info-dialog content only; modal lifecycle is owned by game-modal/app-shell. -->
<info-dialog class="info-content-wrap">
  <header class="fm-header" if={ state.title || state.meta }>
    <div class="fm-header-copy">
      <h2 if={ state.title }>{ state.title }</h2>
      <raw-html if={ state.meta } content={ state.meta } mode="prose" class="fm-meta"></raw-html>
    </div>
    <button class="close-btn modal-close-hit" aria-label="Close info dialog" onclick={ closeDialog }>X</button>
  </header>

  <raw-html if={ state.desc } content={ state.desc } mode="prose" class="fm-desc"></raw-html>

  <div if={ state.media } class="fm-media-shell modal-media-slot float-media">
    <div if={ state.media.imageUrl } class="fm-image-wrap">
      <img class="fm-image" src={ state.media.imageUrl } alt="">
    </div>
    <div if={ state.media.imageLoading && !state.media.imageUrl } class="fm-image-placeholder">
      <span>···</span>
    </div>
    <div if={ !state.media.imageLoading && !state.media.imageUrl } class="fm-image-fallback">
      <span>{ state.media.fallbackLabel || '?' }</span>
    </div>
  </div>

  <div if={ state.memories.length } class="fm-memories">
    <div class="fm-cards-label">MEMORIES</div>
    <ul class="memory-list">
      <li each={ mem in state.memories }>{ mem }</li>
    </ul>
  </div>

  <div if={ state.npcs.length } class="fm-cards">
    <div class="fm-cards-label">WHO IS / WAS THERE</div>
    <div
      each={ npc in state.npcs }
      class="fm-card npc { state.allowEntityInteraction ? '' : 'disabled' }"
      onclick={ () => state.allowEntityInteraction && this.props.content?.onOpenNpc?.(npc) }
    >
      <b>{ npc.name }</b>
      <span class="fm-card-sub">{ npc.identity }</span>
    </div>
  </div>

  <div if={ state.items.length } class="fm-cards">
    <div class="fm-cards-label">WHAT IS / WAS THERE</div>
    <div
      each={ item in state.items }
      class="fm-card item { state.allowEntityInteraction ? '' : 'disabled' }"
      onclick={ () => state.allowEntityInteraction && this.props.content?.onOpenItem?.(item) }
    >
      <object-visual name={ item } size="md"></object-visual>
      <span class="fm-card-arrow">▼</span>
    </div>
  </div>

  <div if={ state.quests.length } class="fm-quests">
    <div class="fm-cards-label">RELATED QUESTS</div>
    <ul class="quest-list">
      <li each={ quest in state.quests }>
        <b>{ quest.title }</b>: { quest.desc }
      </li>
    </ul>
  </div>

  <footer if={ state.actions.length } class="modal-actions">
    <div class="fm-chips">
      <button
        each={ action in state.actions }
        class="fm-chip { action.danger ? 'danger' : '' }"
        onclick={ () => onAction(action) }
      >{ action.label }</button>
    </div>
  </footer>

  <script>
    import { playEffect } from '../../../engine/audio.js'
    import { runUiAction } from '../../action-manager.ts'
    import { resolveDialogImage } from '../../dialog/dialog-media.ts'
    import ObjectVisual from '../object-visual.riot'
    import RawHtml from '../raw-html.riot'

    export default {
      components: { ObjectVisual, RawHtml },
      state: {
        title: '',
        desc: '',
        meta: '',
        actions: [],
        memories: [],
        npcs: [],
        items: [],
        quests: [],
        allowEntityInteraction: true,
        media: null,
      },

      onMounted() {
        this.syncState(this.props)
        this.update()
        this.syncMedia()
      },

      onBeforeUpdate(props) {
        this.syncState(props)
      },

      onUpdated() {
        this.syncMedia()
      },

      syncState(props) {
        const c = props.content ?? {}
        const options = props.options ?? {}
        this.state.title = String(options.title ?? c.title ?? '').trim()
        this.state.desc = String(options.desc ?? c.desc ?? '').trim()
        this.state.meta = String(options.meta ?? c.meta ?? '').trim()
        this.state.actions = Array.isArray(options.actions) ? options.actions : []
        this.state.memories = Array.isArray(c.memories) ? c.memories : []
        this.state.npcs = Array.isArray(c.npcs) ? c.npcs : []
        this.state.items = Array.isArray(c.items) ? c.items : []
        this.state.quests = Array.isArray(c.quests) ? c.quests : []
        this.state.allowEntityInteraction = c.allowEntityInteraction !== false
        
        if (!this.state.media) {
          this.state.media = c.media ?? null
        }
      },

      async syncMedia() {
        const media = this.props.content?.media || null
        if (!media) {
          if (this.state.media) this.update({ media: null })
          this.mediaKey = ''
          return
        }

        const directUrl = media.imageUrl ?? null
        const imageKind = media.imageKind
        const imageSeed = media.imageSeed
        const mediaKey = directUrl ? `url:${directUrl}` : `${String(imageKind ?? '')}:${String(imageSeed ?? '')}:${String(media.fallbackLabel ?? '')}`
        if (mediaKey === this.mediaKey) return
        this.mediaKey = mediaKey

        if (directUrl || !imageKind || !imageSeed) {
          this.update({ media: { ...media, imageUrl: directUrl, imageLoading: false } })
          return
        }

        this.update({ media: { ...media, imageUrl: null, imageLoading: true } })
        try {
          const imageUrl = await resolveDialogImage(imageKind, imageSeed)
          if (this.mediaKey !== mediaKey) return
          this.update({ media: { ...media, imageUrl, imageLoading: false } })
        } catch (error) {
          console.error('Dialog media load failed:', error)
          if (this.mediaKey !== mediaKey) return
          this.update({ media: { ...media, imageUrl: null, imageLoading: false } })
        }
      },

      async closeDialog() {
        await this.props.requestModalClose?.('close-button')
      },

      async onAction(action) {
        playEffect('ui_click')
        const closed = await this.props.requestModalClose?.('action')
        if (closed === false) return
        await runUiAction(action, { initiatorId: 'info-dialog' })
      },
    }
  </script>

  <style>
    info-dialog {
      display: block; /* Important for float */
      overflow: auto; /* Clearfix */
    }

    .fm-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .fm-header-copy {
      min-width: 0;
    }

    .fm-header h2 {
      margin: 0 0 var(--space-xs);
    }

    .float-media {
      float: left;
      width: 40% !important;
      margin-right: var(--space-md);
      margin-bottom: var(--space-xs);
      border-radius: 0.5rem;
      overflow: hidden;
      border: 1px solid var(--border);
    }

    .fm-memories {
      margin-top: 0; /* Let it wrap */
    }

    .memory-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .memory-list li {
      font-size: 0.95rem;
      color: var(--text-prose);
      font-family: var(--font-narrative);
      line-height: 1.5;
      position: relative;
      margin-bottom: var(--space-xs);
    }

    .fm-quests {
      margin-top: var(--space-md);
      clear: both; /* Optional: force quests below float if they feel too crowded */
    }

    .quest-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .quest-list li {
      font-size: 0.85rem;
      color: var(--text-prose, #e2e8f0);
      font-family: var(--font-narrative);
      line-height: 1.4;
      padding-left: var(--space-md);
      position: relative;
      transition: transform 0.16s ease, color 0.16s ease;
      transform-origin: left center;
    }

    .quest-list li b {
      color: var(--gold, var(--gold));
    }

    .quest-list li::before {
      content: '‣';
      position: absolute;
      left: 0;
      color: var(--gold);
    }

    .fm-card.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    /* Improved text colors and readability */
    .fm-cards-label {
      color: var(--gold);
      letter-spacing: 0.1em;
      font-weight: 800;
      font-size: var(--type-3xs);
      margin-bottom: var(--space-xs);
      margin-top: var(--space-sm);
    }

    @media (max-width: 30rem) {
      .float-media {
        float: none;
        width: 100% !important;
        margin-right: 0;
      }
    }
  </style>
</info-dialog>
